import { Link, useNavigate, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import MovieCard from '@/components/feature/MovieCard';
import EmptyState from '@/components/feature/EmptyState';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import { CATEGORY_META, TYPE_LABELS, formatViews, getMovieById, getRelatedMovies } from '@/utils/movie';
import PlayerDemo from './components/PlayerDemo';

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const movie = id ? getMovieById(id) : undefined;
  const related = movie ? getRelatedMovies(movie, 5) : [];
  const meta = movie ? CATEGORY_META[movie.type] : null;

  useDocumentMeta({
    title: movie ? `${movie.title} - 丝瓜视频` : '影视内容未找到 - 丝瓜视频',
    description: movie
      ? `${movie.title}（${movie.year}·${movie.region}）：${movie.synopsis.slice(0, 60)}… 丝瓜视频提供影片简介、类型、主演等信息。`
      : '未找到对应的影视内容，返回丝瓜视频首页浏览更多精彩内容。',
    keywords: movie ? `${movie.title},${TYPE_LABELS[movie.type]},丝瓜视频,${movie.genres.join(',')}` : '丝瓜视频',
  });

  if (!movie || !meta) {
    return (
      <SiteLayout>
        <div className="mx-auto w-full max-w-[1360px] px-4 py-20 md:px-6">
          <EmptyState
            icon="ri-film-line"
            title="影视内容未找到"
            description="很抱歉，你访问的影视内容不存在或已下线。"
          />
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="whitespace-nowrap rounded-md border border-background-300 px-6 py-3 text-sm font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-600"
            >
              返回上一页
            </button>
            <Link to="/" className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 hover:bg-primary-600">
              返回首页
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="relative w-full overflow-hidden bg-primary-950">
        <div className="absolute inset-0">
          <img src={movie.backdrop || movie.poster} alt={`${movie.title} 背景`} className="h-full w-full object-cover object-top opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/85 to-primary-950/60" />
        </div>
        <div className="relative mx-auto w-full max-w-[1360px] px-4 py-8 md:px-6 md:py-12">
          <nav className="flex flex-wrap items-center gap-1.5 text-sm text-primary-100">
            <Link to="/" className="hover:text-accent-300">首页</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <Link to={`/category/${movie.type}`} className="hover:text-accent-300">{meta.label}</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-background-50">{movie.title}</span>
          </nav>

          <div className="mt-6 flex flex-col gap-6 md:flex-row md:gap-8">
            <div className="h-[300px] w-[200px] shrink-0 overflow-hidden rounded-lg border border-background-50/10 md:h-[366px] md:w-[244px]">
              <img
                src={movie.poster}
                alt={`${movie.title} 海报`}
                title={`${movie.title} - 丝瓜视频`}
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-primary-500 px-2.5 py-1 text-xs font-semibold text-background-50">
                  {TYPE_LABELS[movie.type]}
                </span>
                <span className="rounded-md bg-background-50/10 px-2.5 py-1 text-xs text-background-50 backdrop-blur">
                  {movie.region}
                </span>
                <span className="rounded-md bg-background-50/10 px-2.5 py-1 text-xs text-background-50 backdrop-blur">
                  {movie.year}
                </span>
              </div>
              <h1 className="mt-4 font-heading text-3xl font-extrabold text-background-50 md:text-4xl">{movie.title}</h1>
              <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-background-100">
                <span className="flex items-center gap-1 font-heading text-lg font-bold text-accent-400">
                  <i className="ri-star-fill" /> {movie.rating.toFixed(1)}
                </span>
                <span className="flex items-center gap-1"><i className="ri-fire-line" /> {formatViews(movie.views)}热度</span>
                <span className="flex items-center gap-1"><i className="ri-calendar-line" /> 更新于 {movie.updateTime}</span>
              </div>
              <dl className="mt-5 space-y-2 text-sm">
                <div className="flex gap-3">
                  <dt className="w-14 shrink-0 text-primary-200">导演</dt>
                  <dd className="text-background-50">{movie.director}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-14 shrink-0 text-primary-200">主演</dt>
                  <dd className="text-background-50">{movie.cast.join('、')}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-14 shrink-0 text-primary-200">类型</dt>
                  <dd className="flex flex-wrap gap-1.5">
                    {movie.genres.map((g) => (
                      <span key={g} className="rounded bg-background-50/10 px-2 py-0.5 text-xs text-background-50">{g}</span>
                    ))}
                  </dd>
                </div>
              </dl>
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href="#player"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-play-circle-line text-lg" /> 前往播放演示
                </a>
                <Link
                  to={`/category/${movie.type}`}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-6 py-3 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
                >
                  <i className="ri-apps-2-line" /> 所属分类：{meta.label}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1360px] px-4 py-8 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <section id="player">
              <h2 className="mb-4 font-heading text-xl font-bold text-foreground-950">播放演示区</h2>
              <PlayerDemo poster={movie.backdrop || movie.poster} title={movie.title} />
            </section>

            <section className="mt-10">
              <h2 className="mb-4 font-heading text-xl font-bold text-foreground-950">剧情简介</h2>
              <p className="text-sm leading-loose text-foreground-700 md:text-base">{movie.synopsis}</p>
            </section>
          </div>

          <aside className="lg:col-span-1">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <i className="ri-movie-2-line text-primary-600" /> 相关推荐
            </h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-2">
              {related.map((item) => (
                <MovieCard key={item.id} movie={item} />
              ))}
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}